/**
 * Outpost SDK for browsers.
 *
 * `init` is intended to be the first thing an application runs. Everything it
 * installs is defensive: a failure inside the SDK must never surface as a
 * failure in the host page.
 */

import {
  Client,
  levelFromConsoleMethod,
  type ClientMeta,
  type OutpostOptions,
} from '@peaky-ai/outpost-core';
import type {
  Breadcrumb,
  CaptureHint,
  EventUser,
  Level,
  OutpostEvent,
} from '@peaky-ai/outpost-core';

export const SDK_NAME = 'outpost-browser';
export const SDK_VERSION = '0.1.0';

const META: ClientMeta = { name: SDK_NAME, version: SDK_VERSION, platform: 'javascript' };

export interface BrowserOptions extends OutpostOptions {
  /** Capture uncaught errors and unhandled rejections. Defaults to true. */
  captureGlobalErrors?: boolean;
  /** Record console calls as breadcrumbs. Defaults to true. */
  captureConsole?: boolean;
  /** Record fetch/XHR calls as breadcrumbs. Defaults to true. */
  captureFetch?: boolean;
  /** Record clicks and navigation as breadcrumbs. Defaults to true. */
  captureInteractions?: boolean;
}

let client: Client | undefined;
let teardown: Array<() => void> = [];

/** Initialise the SDK. Safe to call twice; the second call replaces the first. */
export function init(options: BrowserOptions): Client {
  close();

  client = new Client(options, META);

  if (options.captureGlobalErrors !== false) installGlobalHandlers(client);
  if (options.captureConsole !== false) installConsoleBreadcrumbs(client);
  if (options.captureFetch !== false) installFetchBreadcrumbs(client);
  if (options.captureInteractions !== false) installInteractionBreadcrumbs(client);

  installUnloadFlush(client);
  return client;
}

/** Remove every listener and patch this SDK installed. */
export function close(): void {
  for (const undo of teardown) {
    try {
      undo();
    } catch {
      // Teardown is best effort; a failure here must not block re-init.
    }
  }
  teardown = [];
  client = undefined;
}

export function getClient(): Client | undefined {
  return client;
}

export function captureException(error: unknown, hint?: CaptureHint): Promise<string | null> {
  return client?.captureException(error, hint) ?? Promise.resolve(null);
}

export function captureMessage(message: string, level: Level = 'info'): Promise<string | null> {
  return client?.captureMessage(message, level) ?? Promise.resolve(null);
}

export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  client?.addBreadcrumb(breadcrumb);
}

export function setUser(user: EventUser | undefined): void {
  client?.setUser(user);
}

/** Pass `undefined` to remove the tag. */
export function setTag(key: string, value: string | undefined): void {
  client?.setTag(key, value);
}

export function flush(): Promise<void> {
  return client?.flush() ?? Promise.resolve();
}

function installGlobalHandlers(active: Client): void {
  const onError = (event: ErrorEvent) => {
    void active.captureException(event.error ?? event.message, {
      handled: false,
      mechanism: 'onerror',
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    void active.captureException(event.reason, {
      handled: false,
      mechanism: 'unhandledrejection',
    });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  teardown.push(() => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  });
}

function installConsoleBreadcrumbs(active: Client): void {
  const methods = ['debug', 'info', 'warn', 'error'] as const;
  const originals = new Map<string, unknown>();

  for (const method of methods) {
    const original = console[method];
    originals.set(method, original);

    console[method] = (...args: unknown[]) => {
      active.addBreadcrumb({
        category: 'console',
        level: levelFromConsoleMethod(method),
        message: args.map(stringify).join(' ').slice(0, 500),
      });
      // Always call through: an SDK that swallows console output would be a
      // debugging nightmare for the host application.
      (original as (...a: unknown[]) => void).apply(console, args);
    };
  }

  teardown.push(() => {
    for (const [method, original] of originals) {
      (console as unknown as Record<string, unknown>)[method] = original;
    }
  });
}

function installFetchBreadcrumbs(active: Client): void {
  if (typeof window.fetch !== 'function') return;

  const original = window.fetch;

  // Cast because a patched fetch cannot carry the static members (preconnect)
  // that the DOM lib declares on the original.
  window.fetch = (async (...args: Parameters<typeof fetch>) => {
    const [input, init] = args;
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method ?? 'GET';
    const started = Date.now();

    try {
      const response = await original(...args);
      active.addBreadcrumb({
        category: 'http',
        level: response.ok ? 'info' : 'warning',
        message: `${method} ${url} [${response.status}]`,
        data: { status_code: response.status, duration_ms: Date.now() - started },
      });
      return response;
    } catch (error) {
      active.addBreadcrumb({
        category: 'http',
        level: 'error',
        message: `${method} ${url} failed`,
        data: { duration_ms: Date.now() - started },
      });
      throw error;
    }
  }) as typeof window.fetch;

  teardown.push(() => {
    window.fetch = original;
  });
}

function installInteractionBreadcrumbs(active: Client): void {
  const onClick = (event: MouseEvent) => {
    const target = event.target as Element | null;
    if (!target?.tagName) return;
    active.addBreadcrumb({
      category: 'ui.click',
      message: describeElement(target),
    });
  };

  const onNavigate = () => {
    active.addBreadcrumb({ category: 'navigation', message: window.location.pathname });
  };

  document.addEventListener('click', onClick, { capture: true, passive: true });
  window.addEventListener('popstate', onNavigate);

  teardown.push(() => {
    document.removeEventListener('click', onClick, { capture: true });
    window.removeEventListener('popstate', onNavigate);
  });
}

/**
 * Flush buffered events as the page goes away.
 *
 * `visibilitychange` rather than `unload`: mobile browsers frequently never
 * fire `unload`, which is precisely where dropped reports come from.
 */
function installUnloadFlush(active: Client): void {
  const onHidden = () => {
    if (document.visibilityState === 'hidden') void active.flush();
  };

  document.addEventListener('visibilitychange', onHidden);
  teardown.push(() => document.removeEventListener('visibilitychange', onHidden));
}

function describeElement(element: Element): string {
  const tag = element.tagName.toLowerCase();
  const id = element.id ? `#${element.id}` : '';
  const className =
    typeof element.className === 'string' && element.className
      ? `.${element.className.trim().split(/\s+/).slice(0, 2).join('.')}`
      : '';
  return `${tag}${id}${className}`;
}

function stringify(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

export type { BrowserOptions as Options, CaptureHint, OutpostEvent };
export { Client } from '@peaky-ai/outpost-core';
